import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, ImagePlus, KeyRound, Trash2, Volume2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TONES, playTone } from "@/lib/tones";
import { Avatar3D, useUserImage, refreshPhotoDirectory } from "./UserImage";

function ImagePicker({ kind, onDone, children }) {
  const ref = useRef(null);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try { await api.post(`/me/image/${kind}`, fd); toast.success(kind === "avatar" ? "Profile picture updated" : "Cover image updated"); refreshPhotoDirectory(); onDone(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (<>
    <input ref={ref} type="file" accept="image/*" className="hidden" data-testid={`profile-${kind}-input`} onChange={pick} />
    <span onClick={() => ref.current.click()}>{children}</span>
  </>);
}

function TonePicker({ user, onSaved }) {
  const prefs = user.prefs || { tone: "chime", popup: true };
  const save = async (p) => {
    try { await api.put("/me/prefs", { ...prefs, ...p }); await onSaved(); toast.success("Alert settings saved"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid="tone-settings">
      <div className="flex items-center gap-2 text-sm font-bold"><Volume2 className="h-4 w-4" />Alert & notification tone</div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {TONES.map(([k, l]) => (
          <button key={k} type="button" data-testid={`tone-option-${k}`} onClick={() => { playTone(k); save({ tone: k }); }}
            className={`rounded-lg border px-3 py-2 text-left text-xs font-semibold transition-colors ${prefs.tone === k ? "border-[#15966B] bg-[#15966B]/10 text-[#15966B]" : "border-zinc-200 hover:border-[#35D39A]"}`}>{l}</button>
        ))}
      </div>
      <label className="flex items-center justify-between rounded-lg border border-zinc-200 px-3 py-2 text-sm">
        <span>Show a pop-up when a new notification arrives</span>
        <Switch data-testid="popup-toggle" checked={prefs.popup !== false} onCheckedChange={(v) => save({ popup: v })} />
      </label>
    </div>
  );
}

export function ProfileDialog({ open, onOpenChange, onPassword }) {
  const { user, reloadUser } = useAuth();
  const cover = useUserImage(user, "cover");
  const [busy, setBusy] = useState(false);
  const remove = async (kind) => { setBusy(true); await api.delete(`/me/image/${kind}`); refreshPhotoDirectory(); await reloadUser(); setBusy(false); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="profile-dialog" className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">My profile</DialogTitle>
          <DialogDescription>Your picture, cover image, password and alert sound.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <div data-testid="profile-cover-preview" className="cover-3d h-32 rounded-xl bg-cover bg-center" style={cover ? { backgroundImage: `url(${cover})` } : undefined} />
          <div className="absolute right-2 top-2 flex gap-1">
            <ImagePicker kind="cover" onDone={reloadUser}><Button size="sm" variant="secondary" data-testid="profile-cover-change"><ImagePlus className="mr-1 h-4 w-4" />Cover</Button></ImagePicker>
            {cover && <Button size="sm" variant="secondary" disabled={busy} data-testid="profile-cover-remove" onClick={() => remove("cover")}><Trash2 className="h-4 w-4" /></Button>}
          </div>
          <div className="-mt-10 ml-4 flex items-end gap-3">
            <div className="relative">
              <Avatar3D user={user} size={84} testid="profile-avatar-preview" />
              <ImagePicker kind="avatar" onDone={reloadUser}>
                <button type="button" data-testid="profile-avatar-change" className="absolute bottom-0 right-0 flex h-7 w-7 items-center justify-center rounded-full bg-[#15966B] text-white shadow"><Camera className="h-3.5 w-3.5" /></button>
              </ImagePicker>
            </div>
            <div className="pb-1">
              <div className="font-bold">{user.name}</div>
              {user.avatar_at && <button type="button" disabled={busy} data-testid="profile-avatar-remove" onClick={() => remove("avatar")} className="text-xs text-rose-500">Remove picture</button>}
            </div>
          </div>
        </div>
        <Button variant="outline" data-testid="profile-change-password" onClick={() => { onOpenChange(false); onPassword(); }}><KeyRound className="mr-2 h-4 w-4" />Change my password</Button>
        <TonePicker user={user} onSaved={reloadUser} />
      </DialogContent>
    </Dialog>
  );
}
